import type { WorldData } from './schema.ts';

/**
 * World-asset URL resolution (A4 stage 1 — the first consumer of the ADR-0002 tier-c manifest).
 * PURE module (no three, no DOM): imported by the runtime (mount wires it into EntityCtx), the
 * editor (session imports) and the headless tests. Explicit `.ts` extensions keep the chain
 * loadable under Node --experimental-strip-types (same rule as schema.ts).
 *
 * Convention (A4): world asset FILES live at `public/worlds/<id>/assets/<file>` and the manifest
 * `uri` is RELATIVE to the world dir (`"assets/crate.glb"`), so a world folder is self-contained
 * and the cook/publish pipeline can relocate it wholesale (content-hashed dirs) without touching
 * the manifest.
 */

/**
 * Session asset store: manifest `uri` → object URL for files imported into the EDITOR this
 * session (the file is a browser Blob until the human copies it under public/worlds/<id>/ and
 * commits). A hit here always wins over the served path — the freshly imported bytes are the
 * truth the creator is looking at.
 */
export type SessionAssetStore = ReadonlyMap<string, string>;

const ABSOLUTE_URI = /^(?:[a-z][a-z0-9+.-]*:|\/)/i;

/**
 * ── ⭐ P1 · THE `lib:` SCHEME — HOW A WORLD REACHES THE ASSET LIBRARY (ADR-0003, plan §2.3) ──────
 *
 * One scheme on the EXISTING `uri` field, and that is the whole integration: `"uri": "lib:axon-body-001"`
 * resolves through the AssetService instead of through `/worlds/<id>/`. **No schema field moved**,
 * which is the point — `WorldFileAssetSchema` is inside `worldHash`, and a moved `worldHash` refuses
 * every baked GI atlas on every disk (`tests/renderLightRig.test.ts` pins six worlds' hashes,
 * one of them against the owner's real 46,149-capture `gi/probes.json`). `lib:` already matched
 * `ABSOLUTE_URI` before this existed, so a world authoring one used to get a literal, un-fetchable
 * URL back; it now gets a real one or `null`, and `null` is the placeholder the entity already draws.
 *
 * ⚠️ THE PATTERN IS MIRRORED FROM `src/assets/core/uri.ts`, NOT IMPORTED, for the reason this
 * module's header gives: its chain must load under `node --experimental-strip-types`, and
 * `assets/core/uri.ts` reaches `./catalog` extensionless. `tests/assets.test.ts` holds the two
 * spellings to each other — the `tests/libraryDrift.test.ts` discipline, one track over.
 */
export const LIB_SCHEME = 'lib:';

/** Strict kebab, exactly `parseLibUri`'s rule. Anything else is not a library reference. */
const LIB_ID = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/** `lib:<kebab-id>` → the id; `null` for every other uri (including a malformed `lib:` one). */
export function libIdOf(uri: string): string | null {
  if (!uri.startsWith(LIB_SCHEME)) return null;
  const id = uri.slice(LIB_SCHEME.length);
  return LIB_ID.test(id) ? id : null;
}

/**
 * Resolve a library asset id to a fetchable URL. Mount supplies the AssetService's own
 * (`AssetService.fileUrl`), so the CDN layout is stated in exactly one place; absent ⇒ this
 * runtime has no library and every `lib:` uri resolves `null` (the placeholder, rule 6).
 */
export type LibraryUrlResolver = (id: string) => string | null;

/**
 * Resolve one manifest `uri` for `worldId`: session-store hit first (editor-imported blob),
 * absolute URIs (`https://…`, `/…`) pass through, everything else serves from the world's
 * static dir (`/worlds/<id>/<uri>` — the same root mount fetches world.json from).
 */
export function resolveAssetUrl(
  worldId: string,
  uri: string,
  session?: SessionAssetStore,
  library?: LibraryUrlResolver,
): string | null {
  const hit = session?.get(uri);
  if (hit !== undefined) return hit;
  // `lib:` BEFORE the absolute test — it satisfies it, and passing it through as a literal URL is
  // exactly the un-fetchable answer this scheme exists to replace. The PREFIX claims the uri, not
  // the parsed id: a malformed `lib:Not Kebab` is a library reference we refuse (null → the
  // placeholder), never a scheme that falls through to `fetch('lib:Not Kebab')`.
  if (uri.startsWith(LIB_SCHEME)) {
    const libId = libIdOf(uri);
    return libId === null ? null : library?.(libId) ?? null;
  }
  if (ABSOLUTE_URI.test(uri)) return uri;
  return `/worlds/${worldId}/${uri}`;
}

/**
 * Kebab-case asset id from a filename (editor Import + agentic ingest recipe): extension
 * stripped, non-alphanumeric runs collapsed to single dashes, lowercased — always valid under
 * the manifest's kebab id rule ('asset' when nothing survives).
 */
export function assetIdFromFilename(filename: string): string {
  const stem = filename.replace(/\.[^.]+$/, '');
  const kebab = stem
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return kebab === '' ? 'asset' : kebab;
}

/**
 * Resolve a MESH asset id from a world's manifest to a fetchable URL — the EntityCtx seam decor
 * classes consume. Returns null (never throws) when the id is unknown or the entry is not a
 * mesh; the caller shows its placeholder (constitution rule 6).
 */
export function meshAssetUrl(
  world: Pick<WorldData, 'worldId' | 'assets'>,
  assetId: string,
  session?: SessionAssetStore,
  library?: LibraryUrlResolver,
): string | null {
  const asset = world.assets.find((a) => a.id === assetId);
  if (!asset || asset.kind !== 'mesh') return null;
  return resolveAssetUrl(world.worldId, asset.uri, session, library);
}

/**
 * ⭐ L2 — THE COOKED COLLISION SIDECAR'S URL, BY CONVENTION AND NOT BY MANIFEST.
 *
 * `npm run cook:world -- <id> --collision-only` writes `public/worlds/<id>/collision/<assetId>.col`,
 * and this is the other end of that convention. It is DELIBERATELY not a manifest field: a new key
 * in `WorldFileAssetSchema` moves `worldHash`, and a moved `worldHash` REFUSES the world's baked GI
 * atlas on the next boot (`tests/renderLightRig.test.ts` pins six worlds' hashes). A sidecar that
 * costs a re-bake to introduce is a sidecar nobody introduces. Discovery by convention is what
 * keeps L1 and L2 free of a schema change — which is exactly why it is by convention.
 *
 * `null` (no candidate, and the entity does not even issue the request) when:
 *   — the id is unknown or the entry is not a mesh — there is nothing to cook;
 *   — the `uri` is ABSOLUTE (`https://`, `/...`) — not ours to have cooked, and guessing a path on
 *     someone else's origin is a 404 on a host we do not control;
 *   — the asset is a SESSION-IMPORTED BLOB — an editor Import's bytes exist only in this tab, so
 *     there is no cook for them BY CONSTRUCTION. A `.col` fetched for one would describe whatever
 *     GLB used to live at that id, and the `bytes`/`hash` legs would refuse it a moment later -
 *     correctly, but after a pointless request on every spawn.
 *
 * ── ⭐ P1.5·B2 — TWO NAMESPACES, TWO CONVENTIONS, ONE FUNCTION (plan §13.5) ─────────────────────
 *
 * P1 REFUSED a `lib:` mesh here outright, with the reason that a library asset is not in this
 * world's directory. That reason is still true and the refusal was still wrong: it read the
 * absence of a per-WORLD path as the absence of a cook. A library mesh carries its OWN collision
 * — a `collision` ROLE blob the catalog names — because a mesh two worlds place must not need two
 * per-world cooks, and that portability IS the library's whole claim over `world.assets[]`.
 *
 * So the refusal is REPLACED, not widened: a world asset keeps `/worlds/<id>/collision/<id>.col`,
 * a library asset asks the catalog for its own role, and the entity consumes whichever answered
 * through the SAME `EntityCtx.resolveCollisionUrl` seam and the SAME acceptance legs. An absolute
 * `uri` is still refused, and a session blob is still refused BEFORE either arm — a freshly
 * imported file has no cook in either namespace.
 */
export function collisionSidecarUrl(
  world: Pick<WorldData, 'worldId' | 'assets'>,
  assetId: string,
  session?: SessionAssetStore,
  /**
   * The library's own collision-role resolver — mount wires it from
   * `AssetService.fileUrl(id, 'collision')`, so the CDN layout is stated in exactly one place.
   * Absent (or answering `null`: this catalog's entry carries no `collision` role, which is every
   * library mesh with no prefixed nodes) ⇒ no sidecar, and the entity walks the GLB exactly as it
   * does for an uncooked world asset.
   */
  library?: LibraryUrlResolver,
): string | null {
  const asset = world.assets.find((a) => a.id === assetId);
  if (!asset || asset.kind !== 'mesh') return null;
  if (session?.get(asset.uri) !== undefined) return null;
  if (asset.uri.startsWith(LIB_SCHEME)) {
    // The PREFIX claims the uri, exactly as `resolveAssetUrl` does: a malformed `lib:Not Kebab` is
    // a library reference we refuse, never a path that falls through to the world's directory.
    const libId = libIdOf(asset.uri);
    return libId === null ? null : library?.(libId) ?? null;
  }
  if (ABSOLUTE_URI.test(asset.uri)) return null;
  return `/worlds/${world.worldId}/collision/${assetId}.col`;
}
